// Dates, always in the *event's* timezone.
//
// An attendee reading the schedule on a plane must see the times the conference
// runs on, not the ones their phone happens to be showing. Every formatter here
// therefore takes the timezone explicitly, and falls back to UTC rather than to
// the device — a wrong-but-labelled time is recoverable, a silently local one
// is not.

const withZone = (timezone: string): string => {
  if (!timezone) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: timezone }).format(new Date());
    return timezone;
  } catch {
    return 'UTC';
  }
};

const parse = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
};

const format = (at: Date, timezone: string, options: Intl.DateTimeFormatOptions): string =>
  new Intl.DateTimeFormat('en-GB', { ...options, timeZone: withZone(timezone) }).format(at);

/** "Tue 29 October 2026" */
export const formatDay = (iso: string | null | undefined, timezone: string): string => {
  const at = parse(iso);
  if (!at) return '';
  return format(at, timezone, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
};

/** "09:30" */
export const formatTime = (iso: string | null | undefined, timezone: string): string => {
  const at = parse(iso);
  if (!at) return '';
  return format(at, timezone, { hour: '2-digit', minute: '2-digit', hour12: false });
};

/** "09:30 – 10:15", or just the start when there is no end. */
export const formatTimeRange = (
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  timezone: string,
): string => {
  const start = formatTime(startIso, timezone);
  const end = formatTime(endIso, timezone);
  if (!start) return '';
  return end ? `${start} – ${end}` : start;
};

/**
 * The headline date line for an event.
 *
 * Collapses to one date when it starts and ends the same day, and drops the
 * repeated month when it does not — "29–30 October 2026", not
 * "29 October 2026 – 30 October 2026", which reads like two events.
 */
export const formatEventDates = (
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  timezone: string,
): string => {
  const start = parse(startIso);
  const end = parse(endIso);
  if (!start) return '';
  if (!end) return formatDay(startIso, timezone);

  const sameDay =
    format(start, timezone, { dateStyle: 'short' }) ===
    format(end, timezone, { dateStyle: 'short' });
  if (sameDay) return formatDay(startIso, timezone);

  const sameMonth =
    format(start, timezone, { month: 'long', year: 'numeric' }) ===
    format(end, timezone, { month: 'long', year: 'numeric' });

  if (sameMonth) {
    const from = format(start, timezone, { day: 'numeric' });
    const to = format(end, timezone, { day: 'numeric', month: 'long', year: 'numeric' });
    return `${from}–${to}`;
  }
  const from = format(start, timezone, { day: 'numeric', month: 'long' });
  const to = format(end, timezone, { day: 'numeric', month: 'long', year: 'numeric' });
  return `${from} – ${to}`;
};

/** The timezone as an attendee-facing label — "Times shown in Asia/Kolkata". */
export const timezoneLabel = (timezone: string): string => {
  const zone = withZone(timezone);
  return `Times shown in ${zone.replace(/_/g, ' ')}`;
};
